import { Brand, createBranded } from '@deltasierra/type-utilities';
import { RoleType } from './account';
import { LINKEDIN_ORGANIZATION_URN_PREFIX } from './data';
import { convertLinkedInIdToUrn } from './common';

export type LinkedInOrganizationUrn = Brand<string, 'LinkedInOrganizationUrn'>;
export const LinkedInOrganizationUrn = createBranded<LinkedInOrganizationUrn>((urn: string) => {
    if (!isLinkedInOrganizationUrn(urn)) {
        throw new Error('Invalid LinkedIn URN');
    }
    return urn;
});

export type LinkedInOrganizationId = Brand<string, 'LinkedInOrganizationId'>;
export const LinkedInOrganizationId = createBranded<LinkedInOrganizationId>();

/**
 * Convert a LinkedIn URN string into an ID string. This will throw if the URN is invalid.
 *
 * @param urn - The LinkedIn URN to convert
 * @returns The string ID
 */
export function convertLinkedInOrganizationUrnToId(urn: LinkedInOrganizationUrn): LinkedInOrganizationId {
    if (!isLinkedInOrganizationUrn(urn)) {
        throw new Error('Invalid LinkedIn URN');
    }
    const index = LINKEDIN_ORGANIZATION_URN_PREFIX.endsWith(':')
        ? LINKEDIN_ORGANIZATION_URN_PREFIX.length
        : LINKEDIN_ORGANIZATION_URN_PREFIX.length + 1;
    return urn.slice(index) as LinkedInOrganizationId;
}

/**
 * Check if a value is a LinkedIn organization URN.
 *
 * @param value - The value to check
 * @returns Whether or not the given value is a LinkedIn organization URN
 */
export function isLinkedInOrganizationUrn(value: string): value is LinkedInOrganizationUrn {
    return value.startsWith(LINKEDIN_ORGANIZATION_URN_PREFIX);
}

/**
 * Convert a LinkedIn organization ID to a URN
 *
 * @param id - The ID to convert
 * @returns The converted organization URN
 */
export function convertLinkedInIdToOrganizationUrn(id: LinkedInOrganizationId | string): LinkedInOrganizationUrn {
    return convertLinkedInIdToUrn<LinkedInOrganizationUrn>(id, LINKEDIN_ORGANIZATION_URN_PREFIX);
}

/**
 * @see https://docs.microsoft.com/en-us/linkedin/marketing/integrations/community-management/organizations/organization-access-control
 */
export interface OrganizationAccessControlElement {
    state: 'APPROVED' | 'REQUESTED';
    role: RoleType;
    roleAssignee: string;
    organization: LinkedInOrganizationUrn;
}

/**
 * LinkedIn organization entity.
 *
 * @see https://docs.microsoft.com/en-us/linkedin/marketing/integrations/community-management/organizations/organization-lookup-api#schema
 */
export interface LinkedInOrganizationData {
    /**
     * Unique identifier for the entity
     */
    id: LinkedInOrganizationId;

    /**
     * The locale-specific name of the entity
     */
    localizedName: string;

    /**
     * The locale-specific description of the entity.
     */
    localizedDescription: string;
}
